import {  } from '@reduxjs/toolkit';
import { createTracedAsyncThunk } from '../../../tracing/trace';
import { Api } from '../../Api/Api';
import { IEnogenContractResponse, IStewardshipActivity, IStewardshipCompletionDatesResponse } from '../../Models/Responses/EnogenContractResponse';
import { AppDispatch, RootState } from '../Store';
import { getCurrentActingUser } from '../User/AuthSlice';

export const getSellerEnogenContracts = createTracedAsyncThunk<IEnogenContractResponse[], void, { dispatch: AppDispatch, state: RootState }>(
	'seller/get/enogencontracts',
	async (context, request: void, thunkAPI) =>
	{
		try
		{
			const state = thunkAPI.getState() as RootState;
			const { auth, crops } = state;
			const api = new Api('/api/6', auth.userAuthToken, context);
			const currentUserId = getCurrentActingUser(thunkAPI.getState()).UserId;

			// Always use the year selected in the dropdown (crops.CropYear) so the URL year
			// matches the hierarchyYear query param sourced from the same selection.
			const response = await api.getAsync<IEnogenContractResponse[]>(`users/${currentUserId}/years/${crops.CropYear}/enogenContracts`);

			if (response.ErrorCode === null && response.Success) 
			{
				return response.Data;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

export const getContractStewardshipData = createTracedAsyncThunk<IStewardshipActivity[], null, { dispatch: AppDispatch, state: RootState }>(
	'contract/get/stewardshipdata',
	async (context, request: null, thunkAPI) => 
	{
		try
		{
			const state = thunkAPI.getState() as RootState;
			const { auth, ui, crops } = state;
			const api = new Api('/api/6', auth.userAuthToken, context);
			const currentUserId = getCurrentActingUser(thunkAPI.getState()).UserId;
			const currentContractId = ui.SelectedEnogenContractId;
			const currentYear = crops.CropYear;

			const response = await api.getAsync<IStewardshipActivity[]>(`users/${currentUserId}/years/${currentYear}/enogenContracts/${currentContractId}/stewardshipActivities`);

			if (response.ErrorCode === null && response.Success) 
			{
				return response.Data;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

export const getStewardshipCompletionDates = createTracedAsyncThunk<{data: IStewardshipCompletionDatesResponse, contractId: string}, null, { dispatch: AppDispatch, state: RootState }>(
	'contract/get/stewardshipcompletiondates',
	async (context, request: null, thunkAPI) => 
	{
		try
		{
			const state = thunkAPI.getState() as RootState;
			const { auth, ui, crops } = state;
			const api = new Api('/api/6', auth.userAuthToken, context);
			const requestedYear = crops.CropYear;
			const currentContractId = ui.SelectedEnogenContractId;

			const response = await api.getAsync<IStewardshipCompletionDatesResponse>(`years/${requestedYear}/stewardshipActivities/completionDates`);

			if (response.ErrorCode === null && response.Success) 
			{
				return { data: response.Data, contractId: currentContractId };
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

export interface ISaveStewardshipDataRequest
{
	Type: string;
	StewardshipActivityId: string;
	Planting?: ISaveStewardshipPlantingRequest[];
	Harvest?: ISaveStewardshipHarvestRequest[];
}

export interface ISaveStewardshipPlantingRequest
{
	FieldId: string;
	Date: string;
	TotalPlantedAcres: string;
	PlantingRate: string;
	EstBuAcre: string;
}

export interface ISaveStewardshipHarvestRequest
{
	FieldId: string;
	TotalHarvest: string;
	TotalAcres: string;
	YieldPerAcre: string;
	UnitType: string;
	HarvestDate: string;
}

export const saveContractStewardshipData = createTracedAsyncThunk<IStewardshipActivity, ISaveStewardshipDataRequest, { dispatch: AppDispatch, state: RootState }>(
	'contract/save/stewardshipdata',
	async (context, request: ISaveStewardshipDataRequest, thunkAPI) => 
	{
		try
		{
			const state = thunkAPI.getState() as RootState;
			const { auth, ui, crops } = state;
			const api = new Api('/api/6', auth.userAuthToken, context);
			const currentUserId = getCurrentActingUser(thunkAPI.getState()).UserId;
			const currentContractId = ui.SelectedEnogenContractId;
			const requestedYear = crops.CropYear;

			const response = await api.postAsync<IStewardshipActivity>(`users/${currentUserId}/years/${requestedYear}/enogenContracts/${currentContractId}/stewardshipActivities`, request);

			if (response.ErrorCode === null && response.Success)
			{
				return response.Data;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

export interface IStewardshipReplantOrLostRequest
{
	StewardshipActivityId: string;
	Date: string;
	Notes: string;
	Fields: IStewardshipReplantOrLostFieldRequest[];
}
export interface IStewardshipReplantOrLostFieldRequest
{
	FieldName: string; // Used in the display on the Replant/Loss Modal but not necessary data on the Save
	FieldId: string;
	TotalAcres: string; // Used in the display on the Replant/Lost Modal but not necessary data on the Save
	Acres: string;
}
export interface IStewardshipReplantResponse
{
	StewardshipReplantId: string;
    StewardshipId: string; // This is the Stewardship Activity Id
    FieldId: string;
    ReplantedDate: string;
    AcresReplanted: string;
    Notes: string;
    Created: string;
    Modified: string;
}

export const updateContractStewardshipReplant = createTracedAsyncThunk<boolean, IStewardshipReplantOrLostRequest, { dispatch: AppDispatch, state: RootState }>(
	'contract/save/replant',
	async (context, request: IStewardshipReplantOrLostRequest, thunkAPI) => 
	{
		try
		{
			const state = thunkAPI.getState() as RootState;
			const { auth, ui, crops } = state;
			const api = new Api('/api/6', auth.userAuthToken, context);
			const currentUserId = getCurrentActingUser(thunkAPI.getState()).UserId;
			const currentContractId = ui.SelectedEnogenContractId;
			const requestedYear = crops.CropYear;

			const response = await api.postAsync<IStewardshipReplantResponse[]>(`users/${currentUserId}/years/${requestedYear}/enogenContracts/${currentContractId}/stewardshipActivities/replant`, request);

			if (response.ErrorCode === null && response.Success)
			{
				// Now retrieve the stewardship data again
				thunkAPI.dispatch(getContractStewardshipData());
				return true;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

export interface IStewardshipFieldLostResponse
{
	StewardshipFieldLostId: string;
    StewardshipId: string; // This is the Stewardship Activity Id
    FieldId: string;
    LostDate: string;
    AcresLost: string;
    Notes: string;
    Created: string;
    Modified: string;
}

export const updateContractStewardshipFieldLoss = createTracedAsyncThunk<boolean, IStewardshipReplantOrLostRequest, { dispatch: AppDispatch, state: RootState }>(
	'contract/save/fieldloss',
	async (context, request: IStewardshipReplantOrLostRequest, thunkAPI) => 
	{
		try
		{
			const state = thunkAPI.getState() as RootState;
			const { auth, ui, crops } = state;
			const api = new Api('/api/6', auth.userAuthToken, context);
			const currentUserId = getCurrentActingUser(thunkAPI.getState()).UserId;
			const currentContractId = ui.SelectedEnogenContractId;
			const requestedYear = crops.CropYear;

			const response = await api.postAsync<IStewardshipFieldLostResponse[]>(`users/${currentUserId}/years/${requestedYear}/enogenContracts/${currentContractId}/stewardshipActivities/fieldlost`, request);

			if (response.ErrorCode === null && response.Success)
			{
				// Now retrieve the stewardship data again
				thunkAPI.dispatch(getContractStewardshipData());
				return true;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);


export const ResendDeliveryType =
{
	Email: 'email',
	Phone: 'phone'
};
export interface IResendForSigningRequest
{
	DeliveryType: string;
	Phone: string;
	Email: string;
}

export const resendForSigning = createTracedAsyncThunk<boolean,{contractId: string, requestBody: IResendForSigningRequest}, { dispatch: AppDispatch, state: RootState }>(
	'contract/resendsigning',
	async (context, request: {contractId: string, requestBody: IResendForSigningRequest}, thunkAPI) => 
	{
		try
		{
			const state = thunkAPI.getState() as RootState;
			const { auth, ui, crops } = state;
			const api = new Api('/api/6', auth.userAuthToken, context);
			const currentUserId = getCurrentActingUser(thunkAPI.getState()).UserId;
			const requestedYear = crops.CropYear;

			const response = await api.postAsync<boolean>(`users/${currentUserId}/years/${requestedYear}/enogenContracts/${request.contractId}/resend`, request.requestBody);

			if (response.ErrorCode === null && response.Success)
			{
				return response.Data;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);